import { Tooltip, Typography, TypographyProps } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'

interface P extends Omit<TypographyProps, 'children' | 'ref' | 'noWrap' | 'maxWidth' | 'overflow' | 'textOverflow'> {
    children: string
    charLimit?: number
    truncateAtWidth?: string | number
}

const SmartTypography = ({ children, charLimit, truncateAtWidth, ...typographyProps }: P) => {
    const textRef = useRef<HTMLElement>(null)
    const [isTruncated, setIsTruncated] = useState(false)

    const displayedText = charLimit && children?.length > charLimit
        ? children.slice(0, charLimit) + '…'
        : children

    const checkTruncation = useCallback(() => {
        const el = textRef.current
        if (!el) return
        if (charLimit) {
            setIsTruncated(children?.length > charLimit)
        } else {
            setIsTruncated(el.scrollWidth > el.clientWidth)
        }
    }, [children, charLimit])

    useEffect(() => {
        const el = textRef.current
        if (!el) return
        checkTruncation()
        const observer = new ResizeObserver(() => checkTruncation())
        observer.observe(el)
        return () => observer.unobserve(el)
    }, [checkTruncation])

    return (
        <Tooltip
            title={isTruncated ? children : undefined}
            disableHoverListener={!isTruncated}
            arrow>
            <Typography
                {...typographyProps}
                ref={textRef}
                sx={{
                    ...typographyProps.sx,
                    ...(truncateAtWidth && { maxWidth: truncateAtWidth }),
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden'
                }}>
                {displayedText}
            </Typography>
        </Tooltip>
    )
}

export default SmartTypography
